import { constants, price as priceUtil } from '@eti/utils';
import { useProperty, useTranslation } from '@eti/providers';
import { TICKET_TYPES } from '../constants';
import type { Ticket } from '../model';
import type {
  AvailableExtraProductsType,
  BoundsType,
  PreSelectedExtraProducts,
  TravelerPrices,
} from '../../../../models';
import {
  getFlexibleTicketConfig,
  getCFARTicketConfig,
  isFlexibleTicketAllowedForCarriers,
  isCFARTicketAllowedForCarriers,
  usePriceDisplayType,
  getTripDisplayPrice,
} from '../../../../utils/resultUtils';
import { preSelectedExtraProductsData } from '../../preSelectedProducts/preSelectedExtraProductsUtils';

const { SEARCH_RESULT_FLEX_BEHAVIOR_OPTIONS } = constants;

export const useGetTickets = (
  availableExtraProducts: AvailableExtraProductsType,
  bounds: BoundsType,
  preSelectedExtraProducts: PreSelectedExtraProducts[],
  travelerPrices: TravelerPrices[],
  discountAmount = 0,
): Ticket[] => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { priceDisplayType, priceDisplayText } = usePriceDisplayType();

  const { totalPricePreSelectedProducts } = preSelectedExtraProductsData(preSelectedExtraProducts);

  const { CANCEL_FOR_ANY_REASON_TICKET, FLEXIBLE_TICKET, STANDARD_TICKET } = TICKET_TYPES;

  const standardTicketPrice = priceUtil.formatPrice(
    getTripDisplayPrice(priceDisplayType)(travelerPrices) -
      discountAmount +
      totalPricePreSelectedProducts,
  );

  const tickets: Ticket[] = [
    {
      pricePerPerson: standardTicketPrice,
      priceDisplayText,
      ticketTitle: t('Air.Result.StandardTripType'),
      ticketType: STANDARD_TICKET,
    },
  ];

  const flexBehaviour = p('IbeClient.SearchResult.Flex.Behaviour');
  const behaviorConfig = {
    [SEARCH_RESULT_FLEX_BEHAVIOR_OPTIONS.AIR_REBOOKING]: {
      isAllowed: isFlexibleTicketAllowedForCarriers(bounds, p('Result.HideFlexTicket.Carriers')),
      getConfig: getFlexibleTicketConfig(availableExtraProducts),
      getPrice: (config: any) =>
        getTripDisplayPrice(priceDisplayType)(
          travelerPrices,
          config?.sellSpecification?.sellPriceTravelers,
        ),
      ticketType: FLEXIBLE_TICKET,
      ticketTitle: t('Result.FlexTicket.Button.Label'),
    },
    [SEARCH_RESULT_FLEX_BEHAVIOR_OPTIONS.CANCEL_FOR_ANY_REASON]: {
      isAllowed: isCFARTicketAllowedForCarriers(bounds, p('Result.HideCFAR.Carriers')),
      getConfig: getCFARTicketConfig(availableExtraProducts),
      getPrice: (config: any) =>
        getTripDisplayPrice(priceDisplayType)(
          travelerPrices,
          [],
          config?.sellSpecification?.price?.price?.value,
        ),
      ticketType: CANCEL_FOR_ANY_REASON_TICKET,
      ticketTitle: t('cancelForAnyReasonTitle'),
    },
  };

  const config = behaviorConfig[flexBehaviour];
  if (config?.isAllowed) {
    const ticketConfig = config.getConfig;
    if (ticketConfig) {
      const pricePerPerson = priceUtil.formatPrice(
        config.getPrice(ticketConfig) - discountAmount + totalPricePreSelectedProducts,
      );
      tickets.push({
        pricePerPerson,
        priceDisplayText,
        ticketTitle: config.ticketTitle,
        ticketType: config.ticketType,
        ...(flexBehaviour === SEARCH_RESULT_FLEX_BEHAVIOR_OPTIONS.AIR_REBOOKING && {
          flexibleTicketPrices: ticketConfig.sellSpecification?.sellPriceTravelers,
        }),
        ...(flexBehaviour === SEARCH_RESULT_FLEX_BEHAVIOR_OPTIONS.CANCEL_FOR_ANY_REASON && {
          cfarTicketPrices: ticketConfig.sellSpecification?.price?.price?.value,
        }),
      });
    }
  }

  return tickets;
};
